import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { REWORK_REASON_CODE } from '@/constants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Filter, RefreshCw, Calendar, ChevronDown, ChevronRight, Eye, Edit2, Send, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const statusStyles = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-200',
  APPROVED: 'bg-green-100 text-green-700 border-green-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
};

function computeReworkDelta(run) {
  if (!run || !Array.isArray(run.parts)) return 0;
  let delta = 0;
  run.parts.forEach((p) => {
    if (!p) return;
    const breakdown = p.rejection_breakdown || [];
    let rwRejected = 0;
    breakdown.forEach((row) => {
      if (!row) return;
      const code = (row.reason_code || '').toUpperCase();
      if (code === REWORK_REASON_CODE) {
        const qty = row.qty != null ? Number(row.qty) || 0 : 0;
        if (qty > 0) rwRejected += qty;
      }
    });
    const consumed = p.rework_consumed != null ? Number(p.rework_consumed) || 0 : 0;
    delta += rwRejected - consumed;
  });
  return delta;
}

function groupRunsByDate(runs) {
  const byDate = {};
  runs.forEach((run) => {
    if (!byDate[run.date]) byDate[run.date] = [];
    byDate[run.date].push(run);
  });
  return Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function ProductionRunsHistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterMachine, setFilterMachine] = useState('ALL');
  const [filterShift, setFilterShift] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  const [page, setPage] = useState(1);
  const pageSize = 28;
  const [total, setTotal] = useState(0);

  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  const [expandedDates, setExpandedDates] = useState(new Set());

  const canSubmit = user?.role === 'SUPERVISOR' || user?.role === 'MANAGER' || user?.role === 'OWNER';
  const canApprove = user?.role === 'MANAGER' || user?.role === 'OWNER';

  useEffect(() => {
    api
      .get('/machines')
      .then((res) => setMachines(res.data ?? []))
      .catch(() => setMachines([]));
  }, []);

  useEffect(() => {
    const state = location.state || {};
    const saved = state.filters;
    if (saved) {
      setDateFrom(saved.dateFrom || '');
      setDateTo(saved.dateTo || '');
      setFilterMachine(saved.filterMachine || 'ALL');
      setFilterShift(saved.filterShift || 'ALL');
      setFilterStatus(saved.filterStatus || 'ALL');
      const initialPage = saved.page || 1;
      setPage(initialPage);
      fetchRuns(initialPage, saved.filterStatus || 'ALL');
      return;
    }
    const statusFromState = state.status;
    if (statusFromState && typeof statusFromState === 'string') {
      setFilterStatus(statusFromState);
      fetchRuns(1, statusFromState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRuns = async (pageOverride, statusOverride) => {
    const targetPage = pageOverride || page || 1;
    setLoading(true);
    setHasSearched(true);
    try {
      const params = {};
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      if (filterMachine && filterMachine !== 'ALL') params.machine_id = filterMachine;
      if (filterShift && filterShift !== 'ALL') params.shift = filterShift;
      const statusToUse = statusOverride || filterStatus;
      if (statusToUse && statusToUse !== 'ALL') params.status = statusToUse;
      params.page = targetPage;
      params.page_size = pageSize;

      const res = await api.get('/production-runs', { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setRuns(data);
      const headerCount = Number(res.headers['x-total-count'] || res.headers['X-Total-Count'] || 0);
      setTotal(headerCount || data.length);
      setPage(targetPage);
      navigate('.', {
        replace: true,
        state: {
          filters: {
            dateFrom,
            dateTo,
            filterMachine,
            filterShift,
            filterStatus: statusToUse,
            page: targetPage,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFilterMachine('ALL');
    setFilterShift('ALL');
    setFilterStatus('ALL');
    setRuns([]);
    setTotal(0);
    setPage(1);
    setHasSearched(false);
    setExpandedDates(new Set());
  };

  const toggleDate = (date) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const dateGroups = groupRunsByDate(runs);

  const handleSubmit = async (runId) => {
    try {
      await api.post(`/production-runs/${runId}/submit`);
      toast.success('Production run submitted for approval');
      fetchRuns(page);
      window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async (runId) => {
    try {
      await api.post(`/production-runs/${runId}/approve`, {
        action: 'APPROVE',
        comment: '',
      });
      toast.success('Run approved');
      fetchRuns(page);
      window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (runId) => {
    const reason = window.prompt('Rejection reason (optional):', '');
    try {
      await api.post(`/production-runs/${runId}/approve`, {
        action: 'REJECT',
        comment: reason || '',
      });
      toast.success('Run rejected');
      fetchRuns(page);
      window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleApproveGroup = async (dateRuns) => {
    const submitted = dateRuns.filter((r) => r.status === 'SUBMITTED');
    if (!submitted.length || !canApprove) return;
    const ok = window.confirm(`Approve ${submitted.length} submitted run(s) for this date?`);
    if (!ok) return;
    try {
      const promises = submitted.map((run) =>
        api
          .post(`/production-runs/${run.id}/approve`, { action: 'APPROVE', comment: '' })
          .then(
            () => ({ ok: true }),
            (err) => ({ ok: false, err }),
          ),
      );
      const results = await Promise.all(promises);
      const succeeded = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      if (succeeded && !failed.length) {
        toast.success(`Approved ${succeeded} run(s)`);
      } else if (succeeded && failed.length) {
        toast.warning(`Approved ${succeeded} run(s), ${failed.length} failed`);
      } else {
        toast.error('Failed to approve runs');
      }
      fetchRuns(page);
      if (succeeded) window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch {
      toast.error('Failed to approve runs');
    }
  };

  const handleSubmitGroup = async (dateRuns) => {
    const drafts = dateRuns.filter((r) => r.status === 'DRAFT');
    if (!drafts.length || !canSubmit) return;
    const ok = window.confirm(`Submit ${drafts.length} draft run(s) for approval?`);
    if (!ok) return;
    try {
      const promises = drafts.map((run) =>
        api.post(`/production-runs/${run.id}/submit`).then(
          () => ({ ok: true }),
          (err) => ({ ok: false, err }),
        ),
      );
      const results = await Promise.all(promises);
      const succeeded = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      if (succeeded && !failed.length) {
        toast.success(`Submitted ${succeeded} draft run(s) for approval`);
      } else if (succeeded && failed.length) {
        toast.warning(`Submitted ${succeeded} draft run(s), ${failed.length} failed`);
      } else {
        toast.error('Failed to submit draft runs');
      }
      fetchRuns(page);
      if (succeeded) window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch {
      toast.error('Failed to submit draft runs');
    }
  };

  const handleRejectGroup = async (dateRuns) => {
    const submitted = dateRuns.filter((r) => r.status === 'SUBMITTED');
    if (!submitted.length || !canApprove) return;
    const proceed = window.confirm(`Reject ${submitted.length} submitted run(s) for this date?`);
    if (!proceed) return;
    const reason = window.prompt('Rejection reason (optional) for all selected runs:', '');
    try {
      const promises = submitted.map((run) =>
        api
          .post(`/production-runs/${run.id}/approve`, {
            action: 'REJECT',
            comment: reason || '',
          })
          .then(
            () => ({ ok: true }),
            (err) => ({ ok: false, err }),
          ),
      );
      const results = await Promise.all(promises);
      const succeeded = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      if (succeeded && !failed.length) {
        toast.success(`Rejected ${succeeded} run(s)`);
      } else if (succeeded && failed.length) {
        toast.warning(`Rejected ${succeeded} run(s), ${failed.length} failed`);
      } else {
        toast.error('Failed to reject runs');
      }
      fetchRuns(page);
      if (succeeded) window.dispatchEvent(new CustomEvent('approvals-changed'));
    } catch {
      toast.error('Failed to reject runs');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-runs-history-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Production Run History</h1>
          <p className="text-slate-500">
            Search and review historical production runs. Use filters and click Search to load data.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchRuns(page)}
          disabled={!hasSearched}
          data-testid="refresh-pr-history-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="border-slate-200" data-testid="pr-history-filters-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10 w-40"
                data-testid="pr-history-date-from"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10 w-40"
                data-testid="pr-history-date-to"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Machine</label>
              <Select value={filterMachine} onValueChange={setFilterMachine}>
                <SelectTrigger className="h-10 text-sm" data-testid="pr-history-machine">
                  <SelectValue placeholder="All Machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Machines</SelectItem>
                  {machines.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Shift</label>
              <Select value={filterShift} onValueChange={setFilterShift}>
                <SelectTrigger className="h-10 text-sm" data-testid="pr-history-shift">
                  <SelectValue placeholder="All Shifts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Shifts</SelectItem>
                  <SelectItem value="DAY">Day</SelectItem>
                  <SelectItem value="NIGHT">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-10 text-sm" data-testid="pr-history-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SUBMITTED">Submitted</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">&nbsp;</label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="h-10 w-full"
                  data-testid="pr-history-clear-btn"
                >
                  Clear
                </Button>
                <Button
                  onClick={() => fetchRuns(1)}
                  className="h-10 w-full"
                  data-testid="pr-history-search-btn"
                >
                  Search
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="pr-history-table-card">
        <CardHeader>
          <CardTitle className="text-base">Runs by Date</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10" />
                <TableHead>Date</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead>Part No</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead className="text-right">Produced</TableHead>
                <TableHead className="text-right">Rejected</TableHead>
                <TableHead className="text-right">Rework Δ</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!hasSearched ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-10 text-slate-500">
                    Set filters above and click <span className="font-semibold">Search</span> to view production runs.
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : dateGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12">
                    <div className="text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No production runs found</p>
                      <p className="text-sm">Adjust your filters and search again.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                dateGroups.map(([date, dateRuns]) => {
                  const shifts = [...new Set(dateRuns.map((r) => r.shift))].sort();
                  const machinesForDate = [...new Set(dateRuns.map((r) => r.machine_code))].sort();
                  const statusCounts = dateRuns.reduce((acc, r) => {
                    acc[r.status] = (acc[r.status] || 0) + 1;
                    return acc;
                  }, {});
                  const statusSummary = Object.entries(statusCounts)
                    .map(([s, n]) => `${n} ${s}`)
                    .join(', ');
                  const submittedCount = dateRuns.filter((r) => r.status === 'SUBMITTED').length;
                  const draftCount = dateRuns.filter((r) => r.status === 'DRAFT').length;

                  const expanded = expandedDates.has(date);

                  return (
                    <React.Fragment key={date}>
                      <TableRow className="bg-slate-50/80 hover:bg-slate-100">
                        <TableCell className="w-10 p-0 align-top">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => toggleDate(date)}
                            data-testid={`pr-history-expand-${date}`}
                          >
                            {expanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-mono text-sm align-top">{date}</TableCell>
                        <TableCell className="text-sm align-top">{shifts.join(', ')}</TableCell>
                        <TableCell className="text-sm font-mono align-top">
                          {machinesForDate.join(', ')}
                        </TableCell>
                        <TableCell className="text-slate-400 align-top">—</TableCell>
                        <TableCell className="text-slate-400 align-top">—</TableCell>
                        <TableCell className="text-right text-slate-400 align-top">—</TableCell>
                        <TableCell className="text-right text-slate-400 align-top">—</TableCell>
                        <TableCell className="text-sm align-top">
                          {dateRuns.length} run(s): {statusSummary}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex items-center gap-1">
                            {canSubmit && draftCount > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                onClick={() => handleSubmitGroup(dateRuns)}
                                title="Submit all draft runs for this date"
                                data-testid={`pr-history-submit-group-${date}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {canApprove && submittedCount > 0 && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-600 hover:bg-green-50"
                                  onClick={() => handleApproveGroup(dateRuns)}
                                  title="Approve all submitted runs for this date"
                                  data-testid={`pr-history-approve-group-${date}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                                  onClick={() => handleRejectGroup(dateRuns)}
                                  title="Reject all submitted runs for this date"
                                  data-testid={`pr-history-reject-group-${date}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expanded &&
                        dateRuns.map((run) => (
                          <TableRow
                            key={run.id}
                            className="hover:bg-slate-50"
                            data-testid={`pr-history-run-${run.id}`}
                          >
                            <TableCell />
                            <TableCell className="font-mono text-sm">{run.date}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  run.shift === 'DAY'
                                    ? 'bg-yellow-50 text-yellow-700'
                                    : 'bg-indigo-50 text-indigo-700'
                                }
                              >
                                {run.shift}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{run.machine_code}</TableCell>
                            <TableCell className="font-mono">
                              {Array.isArray(run.parts) && run.parts.length > 1
                                ? `Multiple (${run.parts.length})`
                                : (run.part_no || (run.parts && run.parts[0]?.part_no) || '')}
                            </TableCell>
                            <TableCell>{run.operator_name}</TableCell>
                            <TableCell className="text-right font-mono font-medium">
                              {run.metrics.produced_qty.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              <span
                                className={
                                  run.metrics.rejected_qty > 0 ? 'text-red-600' : undefined
                                }
                              >
                                {run.metrics.rejected_qty}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {(() => {
                                const delta = computeReworkDelta(run);
                                if (!delta) return '-';
                                const cls =
                                  delta > 0
                                    ? 'text-amber-600'
                                    : delta < 0
                                    ? 'text-green-700'
                                    : '';
                                return <span className={cls}>{delta > 0 ? `+${delta}` : delta}</span>;
                              })()}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`${statusStyles[run.status] || ''} font-medium`}
                              >
                                {run.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => navigate(`/production-runs/${run.id}`)}
                                  data-testid={`pr-history-view-${run.id}`}
                                >
                                  {run.status === 'DRAFT' ? (
                                    <Edit2 className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </Button>
                                {canSubmit && run.status === 'DRAFT' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleSubmit(run.id)}
                                    data-testid={`pr-history-submit-${run.id}`}
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                )}
                                {canApprove && run.status === 'SUBMITTED' && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-green-600 hover:bg-green-50"
                                      onClick={() => handleApprove(run.id)}
                                      data-testid={`pr-history-approve-${run.id}`}
                                    >
                                      <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                                      onClick={() => handleReject(run.id)}
                                      data-testid={`pr-history-reject-${run.id}`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {hasSearched && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-600">
          <div className="text-center sm:text-left">
            {total > 0 ? (
              <>
                Showing <span className="font-semibold">{runs.length}</span> of{' '}
                <span className="font-semibold">{total}</span> run
                {total !== 1 ? 's' : ''} (page {page} of {totalPages})
              </>
            ) : (
              'No production runs to display'
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => fetchRuns(page - 1)}
                data-testid="pr-history-page-prev"
              >
                Previous
              </Button>
              <span className="text-xs sm:text-sm">
                Page <span className="font-semibold">{page}</span> of{' '}
                <span className="font-semibold">{totalPages}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => fetchRuns(page + 1)}
                data-testid="pr-history-page-next"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

